// src/pages/CheckoutPage.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import { apiRequest } from "../services/api";
import { getActiveCouponsCount } from "../services/coupons.api";


function CheckoutPage() {
  const { lang } = useContext(LanguageContext);
  const { cartItems, subtotal, total, discount } = useCart();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const [hasActiveCoupons, setHasActiveCoupons] = useState(true);

  const [shippingMethod, setShippingMethod] = useState("delivery"); 

  // FORM STATE
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [governorate, setGovernorate] = useState("");
  const [address, setAddress] = useState("");

  const [note, setNote] = useState("");
  const [noteFocused, setNoteFocused] = useState(false); // NEW for animation

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const safeSubtotal = Number(subtotal) || 0;

  const [couponError, setCouponError] = useState(null);
  
  const [shipping, setShipping] = useState({
  fee: 4,
  free_enabled: false,
  free_threshold: 0,
});

useEffect(() => {
  apiRequest("/shipping-settings")
    .then((s) =>
      setShipping({
        fee: s.default_fee,
        free_enabled: s.free_enabled,
        free_threshold: s.free_threshold,
      })
    )
    .catch(() => {});
}, []);

useEffect(() => {
  getActiveCouponsCount()
    .then((res) => {
      setHasActiveCoupons(res.count > 0);
    })
    .catch(() => {
      setHasActiveCoupons(false);
    });
}, []);

  useEffect(() => {
  const loadUserData = async () => {
    const storedUser = localStorage.getItem("esill-user");
    if (!storedUser) return;

    const user = JSON.parse(storedUser);

    setFullName(user.full_name || "");
    setPhone(user.phone || "");
    setEmail(user.email || "");

    // 🔥 NEW: fetch latest address for logged-in user
    try {
      const me = await apiRequest("/users/me");

      if (me.city) setGovernorate(me.city);

      if (me.address) {
        setAddress(me.address);
      }
    } catch (err) {
      console.warn("No saved address found");
    }
  };

  loadUserData();
}, []);

const isFreeDelivery =
  shipping.free_enabled && subtotal >= shipping.free_threshold;

const deliveryFee =
  shippingMethod === "pickup"
    ? 0
    : isFreeDelivery
    ? 0
    : shipping.fee;


  // LEBANON GOVERNORATES LIST
  const LEB_GOV = [
  { en: "Beirut", ar: "بيروت" },
  { en: "Mount Lebanon", ar: "جبل لبنان" },
  { en: "North Lebanon", ar: "لبنان الشمالي" },
  { en: "South Lebanon", ar: "لبنان الجنوبي" },
  { en: "Bekaa", ar: "البقاع" },
  { en: "Baalbek-Hermel", ar: "بعلبك الهرمل" },
  { en: "Nabatieh", ar: "النبطية" },
  { en: "Akkar", ar: "عكار" },
];


  const TEXT = {
    EN: {
      title: "CHECKOUT",
      contact: "Contact Information",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email (optional)",
      required: "All required fields must be filled",
      invalidName: "Please enter a valid name (min 3 letters)",
      invalidPhone: "Please enter a valid Lebanese phone number",
      invalidGov: "Please select a governorate",
      invalidAddress: "Please enter a detailed address",
      invalidEmail: "Invalid email format",
      delivery: "Delivery fees",
      city: "Governorate",
      address: "City / Street / Building / Floor",
      note: "Note (optional)",
      payment: "Payment Method",
      cod: "Cash on Delivery",
      summary: "Order Summary",
      placeOrder: "PLACE ORDER",
      toastSuccess: "Order submitted successfully!",
    },
    AR: {
      title: "إتمام الشراء",
      contact: "معلومات التواصل",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني (اختياري)",
      required: "يرجى ملء جميع الخانات المطلوبة",
      invalidName: "الرجاء إدخال اسم صالح (3 أحرف على الأقل)",
      invalidPhone: "الرجاء إدخال رقم هاتف لبناني صالح",
      invalidGov: "يرجى اختيار المحافظة",
      invalidAddress: "يرجى إدخال عنوان تفصيلي",
      invalidEmail: "البريد الإلكتروني غير صالح",
      delivery: "كلفة التوصيل",
      city: "المحافظة",
      address: "المدينة / الشارع / المبنى / الطابق",
      note: "ملاحظة (اختياري)",
      payment: "طريقة الدفع",
      cod: "الدفع عند الاستلام",
      summary: "ملخص الطلب",
      placeOrder: "إتمام الطلب",
      toastSuccess: "تم إرسال الطلب بنجاح!",
    },
  };

  const t = TEXT[lang];

  // 📌 AUTO-FORMAT LEBANESE PHONE NUMBER
  const formatPhone = (val) => {
    let digits = val.replace(/\D/g, "");
    if (val.startsWith("+961")) digits = "961" + digits.slice(3);

    if (digits.startsWith("961")) {
      const rest = digits.slice(3);
      return `+961 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 8)}`.trim();
    }

    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`;
  };

  const handlePhoneChange = (e) => setPhone(formatPhone(e.target.value));

  // VALIDATION
  const validateFields = () => {
    let newErrors = {};

    if (!fullName.trim() || fullName.trim().length < 3)
      newErrors.fullName = t.invalidName;

    const cleanPhone = phone.replace(/\D/g, "");
    const lebaneseRegex = /^(961|0)?(3|70|71|76|78|79|81)\d{6}$/;
    if (!lebaneseRegex.test(cleanPhone)) newErrors.phone = t.invalidPhone;

    if (shippingMethod === "delivery") {
  if (!governorate) newErrors.governorate = t.invalidGov;

  if (!address.trim() || address.trim().length < 5)
    newErrors.address = t.invalidAddress;
}


    if (email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) newErrors.email = t.invalidEmail;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(t.required);
      return false;
    }
    return true;
  };


  const applyCoupon = async () => {
  if (!couponCode.trim()) return;

  setCouponLoading(true);
  try {
    const res = await apiRequest("/coupons/validate", {
      method: "POST",
      body: JSON.stringify({
        code: couponCode,
        subtotal: subtotal,
      }),
    });

    setCoupon(res);
    setCouponError(null);
    toast.success("Coupon applied");
  } catch (err) {
    setCoupon(null);

    // ✅ Expected coupon errors (silent)
    if (err.status === 400) {
      if (err.message === "unavailable") {
        setCouponError("unavailable");
      } else if (err.message === "expired") {
        setCouponError("expired");
      } else {
        setCouponError("unavailable");
      }
      return;
    }

    // ❗ Unexpected error only
    console.error(err);
    setCouponError("unavailable");
  } finally {
    setCouponLoading(false);
  }
};


  // SUBMIT
  const submitOrder = async () => {
    if (!cartItems.length) {
  toast.error(lang === "EN" ? "Your cart is empty" : "سلة التسوق فارغة");
  return;
}

  if (!validateFields()) return;

  setLoading(true);

  try {
    let token = localStorage.getItem("esill-token");
    const sessionToken = localStorage.getItem("esill-session");

    let authHeaders = null;

    // 🔥 GUEST CHECKOUT FLOW
    if (!token) {
      // 1️⃣ Create guest user
      const guest = await apiRequest("/auth/guest", {
  method: "POST",
  body: JSON.stringify({
    full_name: fullName,
    phone,
    email: email || null,
  }),
});

// 🔔 EXISTING ACCOUNT INFO
if (guest.existed) {
  toast(
    lang === "EN"
      ? `You already have an account with this phone number:\n${guest.user.phone}`
      : `لديك حساب موجود مسبقاً بهذا الرقم:\n${guest.user.phone}`,
    {
      icon: "ℹ️",
      duration: 4000,
    }
  );
}

localStorage.setItem("esill-token", guest.token);
localStorage.setItem("esill-user", JSON.stringify(guest.user));
token = guest.token;
authHeaders = {
  Authorization: `Bearer ${guest.token}`,
};

      // 2️⃣ MERGE SESSION CART INTO USER CART
      if (sessionToken) {
  await apiRequest("/cart/merge", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${guest.token}`,
    },
    body: JSON.stringify({ session_token: sessionToken }),
  });

  localStorage.removeItem("esill-session");
}
    }

    // 3️⃣ PLACE ORDER (now cart is linked to user)
    const payload = {
  full_name: fullName,
  phone,
  email: email || null,
  city: shippingMethod === "delivery" ? governorate : null,
  street: shippingMethod === "delivery" ? address : null,
  notes: note || null,
  payment_method: "cod",
  coupon_code: coupon?.code || null,
  country: "Lebanon",
  shipping_method: shippingMethod, // ✅ NEW
};


   const result = await apiRequest("/orders", {
  method: "POST",
  body: JSON.stringify(payload),
  headers: authHeaders || {},
});



    toast.success(t.toastSuccess);

    const finalSubtotal = subtotal;
const finalDiscount = coupon ? coupon.discount : 0;
const finalTotal =
  finalSubtotal - finalDiscount + deliveryFee;

sessionStorage.setItem(
  "esill-order",
  JSON.stringify({
    ...payload,

    // ✅ ADD ITEMS FOR META
    items: cartItems.map((item) => ({
      public_id: item.public_id,
      qty: item.qty,
      price: item.price,
    })),

    subtotal: finalSubtotal,
    discount: finalDiscount,
    delivery_fee: deliveryFee,
    shipping_method: shippingMethod,
    total: finalTotal,

    itemCount: cartItems.reduce((a, b) => a + b.qty, 0),
    createdAt: new Date().toISOString(),
    order_id: result.order_id,
    order_number: result.order_number,
  })
);



    navigate("/thank-you");
  } catch (err) {
    toast.error(err.message || "Order failed");
    console.error(err);
  } finally {
    setLoading(false);
  }
};



  // Input style helper
  const errorInput = (field) =>
    `checkout-input transition-all ${
      errors[field] ? "border-red-500 bg-red-50 animate-shake" : ""
    }`;

    const getDeliveryMessage = () => {
  return lang === "EN"
    ? "Your order will be delivered within 2 to 3 days"
    : "سيتم توصيل طلبك خلال يومين إلى ثلاثة أيام";
};


  return (
    <div
      className="w-full min-h-screen px-5 pt-[95px] pb-24 bg-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] left-4 text-3xl opacity-60 hover:opacity-100"
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center">{t.title}</h1>
      <div className="h-[2px] w-16 bg-black mx-auto mt-2 mb-6"></div>

      {/* CONTACT */}
      <section className="mb-8">
        <h2 className="font-bold text-lg mb-3">{t.contact}</h2>

        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder={t.name}
          className={errorInput("fullName")}
        />
        {errors.fullName && <p className="text-red-500 text-xs mb-2">{errors.fullName}</p>}

        <input
          dir="ltr"
          value={phone}
          onChange={handlePhoneChange}
          placeholder={t.phone}
          className={errorInput("phone")}
        />
        {errors.phone && <p className="text-red-500 text-xs mb-2">{errors.phone}</p>}

        <input
          value={email || ""}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.email}
          className={errorInput("email")}
        />
        {errors.email && <p className="text-red-500 text-xs mb-2">{errors.email}</p>}
      </section>

      {/* SHIPPING METHOD */}
<section className="mb-8">
  <h2 className="font-bold text-lg mb-3">
    {lang === "EN" ? "Shipping Method" : "طريقة الشحن"}
  </h2>

  <label className="flex items-center gap-3 mb-2 text-sm">
    <input
      type="radio"
      checked={shippingMethod === "delivery"}
      onChange={() => setShippingMethod("delivery")}
    />
    {lang === "EN" ? "Delivery" : "توصيل"}
  </label>

  <label className="flex items-center gap-3 text-sm">
    <input
      type="radio"
      checked={shippingMethod === "pickup"}
      onChange={() => setShippingMethod("pickup")}
    />
    {lang === "EN" ? "Pickup from store" : "استلام من المتجر"}
  </label>
</section>


      {/* DELIVERY */}
   {shippingMethod === "delivery" && (
  <section className="mb-8">
    <h2 className="font-bold text-lg mb-3">{t.delivery}</h2>

    {/* GOVERNORATE */}
    <div className="relative text-center">
      <select
        value={governorate || ""}
        onChange={(e) => setGovernorate(e.target.value)}
        className={`${errorInput(
          "governorate"
        )} appearance-none pr-10 rounded-md border-[#D3C5BE] bg-white shadow-sm focus:ring-2 focus:ring-black text-center`}
      >
        <option value="">{t.city}</option>
        {LEB_GOV.map((gov) => (
          <option key={gov.en} value={gov.en}>
            {lang === "EN" ? gov.en : gov.ar}
          </option>
        ))}
      </select>
      <span className="absolute right-3 top-1/2 -translate-y-1/2">▼</span>
    </div>

    {errors.governorate && (
      <p className="text-red-500 text-xs mb-2">{errors.governorate}</p>
    )}

    {/* ADDRESS */}
    <textarea
      value={address || ""}
      onChange={(e) => setAddress(e.target.value)}
      placeholder={t.address}
      className={`${errorInput("address")} min-h-[80px]`}
    />

    {errors.address && (
      <p className="text-red-500 text-xs mb-2">{errors.address}</p>
    )}
  </section>
)}

    {/* NOTE (ALWAYS VISIBLE: delivery + pickup) */}
<section className="mb-8">
  <div
    className={`
      overflow-hidden transition-all duration-300
      ${noteFocused || note.length > 0 ? "max-h-40" : "max-h-12"}
    `}
  >
    <textarea
      value={note || ""}
      onChange={(e) => setNote(e.target.value)}
      onFocus={() => setNoteFocused(true)}
      onBlur={() => setNoteFocused(note.length > 0)}
      placeholder={t.note}
      className="checkout-input min-h-[48px] rounded-md"
    />
  </div>
</section>



      {/* PAYMENT */}
      <section className="mb-8">
        <h2 className="font-bold text-lg mb-3">{t.payment}</h2>

        <label className="flex items-center gap-3 text-sm">
          <input type="radio" checked readOnly />
          {t.cod}
        </label>
      </section>

      {/* SUMMARY */}
      <section className="mb-10">
        <h2 className="font-bold text-lg mb-3">{t.summary}</h2>

       {cartItems.map((item) => (
  <div key={item.id} className="flex mb-3 items-center gap-3">
    <img
      src={item.image}
      className="w-16 h-16 rounded-md object-cover bg-[#F2EFED]"
      alt=""
    />

    <div className="flex-1">
      <p className="font-medium leading-tight">
  {lang === "EN" ? item.nameEN : item.nameAR}
</p>

{item.public_id && (
  <p className="text-[11px] text-[#9F9087] mt-[2px]">
    {item.public_id}
  </p>
)}


      {/* COLOR DOT + SIZE BADGE */}
      {(item.color || item.size) && (
        <div className="flex items-center gap-3 mt-1">
          {/* COLOR DOT */}
          {item.color && (
            <div
              className="w-3.5 h-3.5 rounded-full border border-[#D3C5BE]"
              title={item.color}
              style={{ backgroundColor: item.color }}
            />
          )}

          {/* SIZE BADGE */}
          {item.size && (
            <span
              className="
                text-[11px] px-2 py-[2px]
                rounded-full border border-[#D3C5BE]
                text-[#2E3135] font-medium
              "
            >
              {lang === "EN" ? `Size ${item.size}` : `مقاس ${item.size}`}
            </span>
          )}
        </div>
      )}

      <p className="opacity-60 text-sm mt-1">
        {item.qty} × ${item.price}
      </p>
    </div>
  </div>
))}
        
        <hr className="my-3" />
        {hasActiveCoupons && (
  <>
    {/* COUPON */}
    <div className="flex gap-2 mb-1">
      <input
        value={couponCode}
        onChange={(e) => {
          setCouponCode(e.target.value);
          setCouponError(null);
        }}
        placeholder={lang === "EN" ? "Coupon code" : "رمز الخصم"}
        className="checkout-input flex-1"
      />
      <button
        onClick={applyCoupon}
        disabled={couponLoading}
        className="px-4 rounded-md bg-black text-white text-sm"
      >
        {couponLoading ? "..." : "Apply"}
      </button>
    </div>

    {/* INLINE ERROR MESSAGE */}
    {couponError && (
      <p className="text-xs mt-1 text-red-600">
        {couponError === "unavailable"
          ? lang === "EN"
            ? "Unavailable code"
            : "رمز غير صالح"
          : lang === "EN"
          ? "Coupon expired"
          : "انتهت صلاحية الكوبون"}
      </p>
    )}
  </>
)}


        {coupon && (
  <div className="checkout-line text-sm line-through opacity-60">
    <span>Subtotal</span>
    <span>${subtotal}</span>
  </div>
)}

<div className="checkout-line">
  <span>{coupon ? "New Subtotal" : "Subtotal"}</span>
  <span>
    $
    {coupon
      ? (subtotal - coupon.discount).toFixed(2)
      : subtotal.toFixed(2)}
  </span>
</div>

{coupon && (
  <div className="checkout-line text-green-600">
    <span>Discount</span>
    <span>- ${coupon.discount.toFixed(2)}</span>
  </div>
)}


        {discount > 0 && (
          <div className="checkout-line">
            <span>Discount</span>
            <span>-${discount}</span>
          </div>
        )}

        <div className="checkout-line">
  <span>
    {shippingMethod === "pickup"
      ? lang === "EN"
        ? "Pickup from store"
        : "استلام من المتجر"
      : t.delivery}
  </span>

  <span
    className={
      shippingMethod === "pickup"
        ? "font-bold text-green-600"
        : isFreeDelivery
        ? "font-bold text-red-600"
        : ""
    }
  >
    {shippingMethod === "pickup"
      ? lang === "EN"
        ? "Free"
        : "مجاني"
      : isFreeDelivery
      ? lang === "EN"
        ? `Free delivery on orders above $${shipping.free_threshold}`
        : `توصيل مجاني للطلبات فوق ${shipping.free_threshold}$`
      : `$${shipping.fee}`}
  </span>
</div>



        <hr className="my-3" />

        <div className="checkout-line font-bold text-lg">
  <span>Total</span>
  <span>
    $
    {(
      (coupon ? subtotal - coupon.discount : subtotal) +
      deliveryFee
    ).toFixed(2)}
  </span>
</div>

      </section>

      {/* SUBMIT BUTTON */}
      <button
        onClick={submitOrder}
        disabled={loading}
        className={`w-full py-4 rounded-md text-white font-semibold text-lg transition
          ${loading ? "bg-black/40" : "bg-black hover:bg-[#222] active:scale-95"}
        `}
      >
        {loading ? "..." : t.placeOrder}
      </button>

      <p className="text-sm text-[#2E3135] mt-4 font-medium text-center">
          {getDeliveryMessage()}
        </p>

      {/* SHAKE ANIMATION */}
      <style>{`
        .animate-shake {
          animation: shake 0.35s linear;
        }
        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          50% { transform: translateX(4px); }
          75% { transform: translateX(-4px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default CheckoutPage;
